import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ShoppingBag,
  Trash2,
  Plus,
  Minus,
  ArrowLeft,
  UtensilsCrossed,
  QrCode,
  CreditCard,
  Banknote,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../App";
import { orderApi } from "../lib/orderApi";
import { Button } from "../components/ui/button";

export default function Cart() {
  const navigate = useNavigate();
  const {
    cart,
    updateQuantity,
    removeFromCart,
    clearCart,
    tableId,
    tableName,
    cartTotal,
  } = useCart();

  const [paymentMethod, setPaymentMethod] = useState("pay_later");
  const [showQR, setShowQR] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  const validateOrder = () => {
    if (!tableId) {
      toast.error("Please select your table first.");
      navigate("/select-table");
      return false;
    }
    if (!cart.length) {
      toast.error("Your cart is empty. Please add items.");
      return false;
    }
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return false;
    }
    return true;
  };

  const submitOrder = async (selectedPaymentMethod) => {
    if (!validateOrder()) return;

    setSubmitting(true);
    try {
      const orderPayload = {
        table_id: tableId,
        items: cart.map((c) => ({
          product_id: c.id,
          quantity: c.quantity,
        })),
        payment_method: selectedPaymentMethod,
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        customer_email: email.trim() || null,
        notes: notes.trim() || null,
      };

      const res = await orderApi.createOrder(orderPayload);
      clearCart();
      toast.success("Order placed successfully! 🎉");
      navigate(`/order/${res.id}`);
    } catch (err) {
      console.error("Order submission failed:", err);
      const detail =
        err.response?.data?.detail ||
        "Could not connect to kitchen server. Please inform counter staff.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
      setShowQR(false);
    }
  };

  if (!cart.length) {
    return (
      <div className="min-h-screen bg-[#FFFDF9] max-w-md mx-auto p-6 flex flex-col justify-center items-center text-center space-y-4">
        <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center text-3xl">
          🍨
        </div>
        <h2 className="text-xl font-bold text-[#2C3E50]">Your Cart is Empty</h2>
        <p className="text-xs sm:text-sm text-stone-500 max-w-xs">
          Looks like you haven't added any natural ice creams, shakes, or desserts yet.
        </p>
        <div className="pt-2 flex flex-col w-full gap-2">
          <Link
            to={tableId ? `/order?table=${tableId}` : "/select-table"}
            className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm py-3 px-6 rounded-2xl shadow transition"
          >
            Browse Menu & Add Items
          </Link>
          <Link
            to="/"
            className="text-xs font-semibold text-stone-500 hover:text-stone-700 py-2"
          >
            ← Back to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF9] pb-24 max-w-lg mx-auto p-4 space-y-5">
      {/* HEADER */}
      <div className="flex items-center justify-between pb-3 border-b border-stone-200">
        <button
          onClick={() => navigate(tableId ? `/order?table=${tableId}` : "/select-table")}
          className="p-2 text-stone-600 hover:bg-stone-100 rounded-full transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div className="text-center">
          <h1 className="font-extrabold text-base text-[#2C3E50]">Order Cart</h1>
          <p className="text-[11px] text-sky-600 font-semibold">
            {tableName || `Table ${tableId}`}
          </p>
        </div>

        <button
          onClick={clearCart}
          className="text-xs text-rose-500 hover:underline font-medium p-1"
        >
          Clear
        </button>
      </div>

      {/* ITEMS LIST */}
      <div className="space-y-2.5">
        <h2 className="text-xs font-bold uppercase tracking-wider text-stone-400">
          Items in Cart ({cart.length})
        </h2>

        {cart.map((item) => (
          <div
            key={item.id}
            className="bg-white p-3.5 rounded-2xl border border-stone-200/80 shadow-xs flex items-center justify-between gap-3"
          >
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-sm text-[#2C3E50] truncate">
                {item.name}
              </h3>
              <p className="text-xs font-extrabold text-sky-600 mt-0.5">
                ₹{Number(item.price).toFixed(2)} × {item.quantity} = ₹
                {(Number(item.price) * item.quantity).toFixed(2)}
              </p>
            </div>

            {/* Quantity controls */}
            <div className="flex items-center gap-1.5 bg-stone-100 rounded-xl p-1 shrink-0">
              <button
                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                className="w-6 h-6 rounded-lg bg-white text-stone-700 font-bold text-xs flex items-center justify-center shadow-xs"
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="font-extrabold text-xs px-2 text-[#2C3E50]">
                {item.quantity}
              </span>
              <button
                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                className="w-6 h-6 rounded-lg bg-sky-500 text-white font-bold text-xs flex items-center justify-center shadow-xs"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>

            <button
              onClick={() => removeFromCart(item.id)}
              className="p-1.5 text-stone-400 hover:text-rose-500 transition"
              title="Remove item"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* CUSTOMER DETAILS FORM */}
      <div className="bg-white p-4 rounded-2xl border border-stone-200/80 shadow-xs space-y-3">
        <h2 className="text-xs font-bold uppercase tracking-wider text-stone-400">
          Your Details
        </h2>

        <div>
          <label className="block text-xs font-semibold text-stone-700 mb-1">
            Customer Name *
          </label>
          <input
            type="text"
            required
            placeholder="Enter your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3.5 py-2 text-xs sm:text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-stone-700 mb-1">
            Phone Number *
          </label>
          <input
            type="tel"
            required
            placeholder="10-digit mobile number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-3.5 py-2 text-xs sm:text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-stone-700 mb-1">
            Special Instructions / Extra Topping Notes (Optional)
          </label>
          <input
            type="text"
            placeholder="e.g. Less sweet, extra cone, serve together"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3.5 py-2 text-xs sm:text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
          />
        </div>
      </div>

      {/* PAYMENT METHOD SELECTION */}
      <div className="bg-white p-4 rounded-2xl border border-stone-200/80 shadow-xs space-y-3">
        <h2 className="text-xs font-bold uppercase tracking-wider text-stone-400">
          Payment Preference
        </h2>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setPaymentMethod("pay_later")}
            className={`p-3 rounded-xl border-2 text-left flex flex-col gap-1 transition ${
              paymentMethod === "pay_later"
                ? "border-sky-500 bg-sky-50/60 shadow-xs"
                : "border-stone-200 bg-stone-50 hover:bg-stone-100"
            }`}
          >
            <div className="flex items-center justify-between">
              <Banknote className="w-4 h-4 text-emerald-600" />
              {paymentMethod === "pay_later" && (
                <CheckCircle2 className="w-3.5 h-3.5 text-sky-600" />
              )}
            </div>
            <span className="font-bold text-xs text-[#2C3E50]">Pay at Counter</span>
            <span className="text-[10px] text-stone-500">Cash or Card when served</span>
          </button>

          <button
            type="button"
            onClick={() => setPaymentMethod("pay_now")}
            className={`p-3 rounded-xl border-2 text-left flex flex-col gap-1 transition ${
              paymentMethod === "pay_now"
                ? "border-sky-500 bg-sky-50/60 shadow-xs"
                : "border-stone-200 bg-stone-50 hover:bg-stone-100"
            }`}
          >
            <div className="flex items-center justify-between">
              <QrCode className="w-4 h-4 text-sky-600" />
              {paymentMethod === "pay_now" && (
                <CheckCircle2 className="w-3.5 h-3.5 text-sky-600" />
              )}
            </div>
            <span className="font-bold text-xs text-[#2C3E50]">Pay via UPI QR</span>
            <span className="text-[10px] text-stone-500">Scan & Pay on mobile</span>
          </button>
        </div>
      </div>

      {/* BILL SUMMARY */}
      <div className="bg-white p-4 rounded-2xl border border-stone-200/80 shadow-xs space-y-2 text-xs sm:text-sm">
        <div className="flex justify-between text-stone-500">
          <span>Subtotal</span>
          <span>₹{cartTotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-stone-500">
          <span>Taxes & Service</span>
          <span className="text-emerald-600 font-semibold">Included</span>
        </div>
        <div className="border-t border-stone-100 pt-2 flex justify-between font-extrabold text-base text-[#2C3E50]">
          <span>Total Amount</span>
          <span className="text-sky-600">₹{cartTotal.toFixed(2)}</span>
        </div>
      </div>

      {/* PLACE ORDER BUTTON */}
      <div>
        <button
          type="button"
          disabled={submitting}
          onClick={() => {
            if (paymentMethod === "pay_now") {
              if (validateOrder()) setShowQR(true);
            } else {
              submitOrder("pay_later");
            }
          }}
          className={`w-full py-4 px-6 rounded-2xl font-bold text-base flex items-center justify-center gap-2 shadow-lg transition-all ${
            submitting
              ? "bg-stone-300 text-stone-500 cursor-not-allowed"
              : "bg-sky-500 hover:bg-sky-600 text-white shadow-sky-500/25 hover:scale-[1.02] active:scale-98"
          }`}
        >
          {submitting ? (
            <span>Placing Order...</span>
          ) : (
            <span>
              {paymentMethod === "pay_now"
                ? "Scan UPI QR & Place Order"
                : `Confirm & Place Order (₹${cartTotal.toFixed(2)})`}
            </span>
          )}
        </button>
      </div>

      {/* QR MODAL */}
      {showQR && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setShowQR(false)}
        >
          <div
            className="bg-white p-6 rounded-3xl text-center max-w-sm w-full space-y-4 shadow-2xl border border-stone-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-1">
              <h3 className="font-extrabold text-lg text-[#2C3E50]">Scan UPI QR to Pay</h3>
              <p className="text-xs text-stone-500">
                Amount to pay: <strong className="text-sky-600 text-sm">₹{cartTotal.toFixed(2)}</strong>
              </p>
            </div>

            <div className="p-3 bg-stone-50 rounded-2xl border border-stone-200 inline-block">
              <img
                src="/upi-qr.jpg"
                alt="UPI Payment QR Code"
                className="w-48 h-48 object-contain mx-auto"
                onError={(e) => {
                  e.target.src = "/logo.png";
                }}
              />
            </div>

            <p className="text-xs text-stone-500 leading-relaxed">
              Pay via Google Pay, PhonePe, Paytm, or BHIM, then tap below to submit your order for kitchen preparation.
            </p>

            <button
              disabled={submitting}
              onClick={() => submitOrder("pay_now")}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm py-3 rounded-xl shadow transition"
            >
              {submitting ? "Confirming..." : "I Have Paid • Place Order"}
            </button>

            <button
              onClick={() => setShowQR(false)}
              className="text-xs text-stone-400 hover:text-stone-600"
            >
              Cancel & Choose Another Payment Method
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
